"use client";

import { useState } from "react";
import PayoutForm, {
  type PayoutSource,
  type SavedRecipientOption,
  type WhitelistRecipientOption,
  type PayoutInitialValues,
  type PayoutPrefill,
} from "./payout-form";
import PayoutStart from "./payout-start";
import InvoiceScreener from "./invoice-screener";

/**
 * Two-step payout creation: an XE-style "you send / recipient gets" amount
 * step first, then the detailed recipient/purpose/compliance form — so
 * clients aren't dropped into a long form before they've even said how much.
 */
export default function PayoutWizard({
  customers,
  defaultCustomerId,
  sources = [],
  actingUser,
  savedRecipients = [],
  whitelistRecipients = [],
  initialRecipient,
  initialSource,
}: {
  customers: Array<{ id: string; name: string }>;
  defaultCustomerId?: string;
  sources?: PayoutSource[];
  actingUser?: { email: string; role: string } | null;
  savedRecipients?: SavedRecipientOption[];
  whitelistRecipients?: WhitelistRecipientOption[];
  /** "saved:<id>" or "wl:<id>" — deep-linked from the Saved/Whitelisted recipients pages' "Pay" buttons, skips straight to the form. */
  initialRecipient?: string;
  /** Funding-source key ("va:<id>" / "w:<id>:<code>") — deep-linked from the Account page's "Withdraw" buttons. */
  initialSource?: string;
}) {
  // Lazy initializer: resolve once on mount so a deep-linked recipient skips
  // PayoutStart entirely, landing straight on PayoutForm with it pre-selected.
  const [initial, setInitial] = useState<PayoutInitialValues | null>(() => {
    if (!initialRecipient) return null;
    if (initialRecipient.startsWith("saved:")) {
      const id = Number(initialRecipient.slice("saved:".length));
      const r = savedRecipients.find((x) => x.id === id);
      if (!r) return null;
      return r.kind === "crypto"
        ? { kind: "crypto", code: "USDC", amount: "", chain: r.chain ?? "base", lockSide: "destination" }
        : { kind: "fiat", code: "USD", amount: "", lockSide: "destination" };
    }
    if (initialRecipient.startsWith("wl:")) {
      const wr = whitelistRecipients.find((x) => x.id === initialRecipient.slice("wl:".length));
      if (!wr) return null;
      return { kind: "fiat", code: "USD", amount: "", lockSide: "destination" };
    }
    return null;
  });
  const [prefill, setPrefill] = useState<PayoutPrefill | null>(null);

  if (!initial) {
    return (
      <div className="flex flex-col items-stretch gap-6 lg:flex-row">
        <div className="flex-1">
          <PayoutStart sources={sources} initialSourceValue={initialSource} onContinue={setInitial} />
        </div>

        <div className="flex items-center gap-3 lg:flex-col">
          <div className="h-px flex-1 lg:h-auto lg:w-px lg:flex-1" style={{ background: "var(--c-hairline)" }} />
          <span className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
            or
          </span>
          <div className="h-px flex-1 lg:h-auto lg:w-px lg:flex-1" style={{ background: "var(--c-hairline)" }} />
        </div>

        <div className="flex-1">
          <InvoiceScreener
            onScreened={(result) => {
              setPrefill(result.prefill);
              setInitial(result.initial);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <PayoutForm
      customers={customers}
      defaultCustomerId={defaultCustomerId}
      sources={sources}
      actingUser={actingUser}
      savedRecipients={savedRecipients}
      whitelistRecipients={whitelistRecipients}
      initial={initial}
      prefill={prefill ?? undefined}
      initialRecipientChoice={initialRecipient}
      onEditAmount={
        initialRecipient
          ? undefined
          : () => {
              setInitial(null);
              setPrefill(null);
            }
      }
    />
  );
}
